import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cloneWithNewIds, createNode, createRow, richTextFromTemplate, isRichTextDoc, type Node, type RichTextDoc } from "@broadpaper/core";
import { useSession, useEditorState, useLayout, useLayoutVersion, useUi, nodeLabel } from "../state/context.js";
import type { EditorActions } from "../state/actions.js";
import { PageView } from "./PageView.js";
import { Overlays } from "./Overlays.js";
import { InlineEditor } from "./InlineEditor.js";
import { nodeIdAt } from "./geometry.js";
import { registerDragHandlers, beginDrag } from "../dnd/drag-manager.js";
import { computeDropTarget, computeInsertion, nodeForDrop } from "../dnd/drag-controller.js";
import { Icon } from "../ui/icons.js";
import { Button } from "../ui/primitives.js";
import { fitZoom, scrollTopForPage } from "../toolbar/zoom.js";
import { Breadcrumb } from "./Breadcrumb.js";
import { DesignLegend } from "./DesignLegend.js";

export function Canvas({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const layout = useLayout();
  const version = useLayoutVersion();
  const mode = useEditorState((s) => s.mode);
  const zoom = useEditorState((s) => s.zoom);
  const selection = useEditorState((s) => s.selection);
  const editingId = useEditorState((s) => s.editingId);
  const hoverId = useEditorState((s) => s.hoverId);
  const drag = useUi((u) => u.drag);
  const guides = useUi((u) => u.guides);
  const reveal = useUi((u) => u.revealRequest);
  const toast = useUi((u) => u.toast);
  const activeSampleId = useUi((u) => u.activeSampleId);
  const sampleLabel = session.sampleSets.find((x) => x.id === activeSampleId)?.label ?? "sample data";
  // Design mode draws a *sample*, and a tooltip quoting a synthesised figure
  // reads exactly like a real one, so interactivity waits for preview unless
  // the host asks otherwise.
  const chartMode = session.options.get().interactiveCharts ?? "preview";
  const interactiveCharts = chartMode === "always" || (chartMode === "preview" && mode === "preview");
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const measureHostRef = useRef<HTMLDivElement>(null);
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 3]);
  const [currentPage, setCurrentPage] = useState(0);
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);
  /** Page index to scroll back to once a new layout lands. */
  const pendingPage = useRef<number | null>(null);

  // Attach the measurer to a hidden host inside the editor (inherits fonts/CSS).
  useEffect(() => {
    if (measureHostRef.current) session.attachMeasurer(measureHostRef.current);
  }, [session]);

  // Fit-to-width on first layout.
  const fitted = useRef(false);
  useEffect(() => {
    if (!layout || fitted.current || !scrollRef.current) return;
    fitted.current = true;
    const o = session.options.get();
    if (typeof o.zoom === "number") session.store.setZoom(o.zoom);
    else {
      const avail = scrollRef.current.clientWidth - 80;
      session.store.setZoom(Math.min(1.25, Math.max(0.3, avail / layout.page.width)));
    }
  }, [layout, session]);

  // Virtualisation: which pages are near the viewport.
  const updateVisible = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc || !layout) return;
    const pageH = layout.page.height * zoom + 28;
    const top = sc.scrollTop - 32;
    const first = Math.max(0, Math.floor(top / pageH) - 1);
    const last = Math.min(layout.paged.totalPages - 1, Math.ceil((top + sc.clientHeight) / pageH) + 1);
    setVisibleRange((r) => (r[0] === first && r[1] === last ? r : [first, last]));
    // Which page you would say you were looking at: the one covering the middle
    // of the viewport, not the one that happens to touch the top edge.
    const middle = sc.scrollTop + sc.clientHeight / 2 - 32;
    setCurrentPage(Math.max(0, Math.min(layout.paged.totalPages - 1, Math.floor(middle / pageH))));
    bump();
  }, [layout, zoom, bump]);
  useEffect(() => {
    updateVisible();
    const sc = scrollRef.current;
    if (!sc) return;
    sc.addEventListener("scroll", updateVisible, { passive: true });
    const ro = new ResizeObserver(updateVisible);
    ro.observe(sc);
    return () => {
      sc.removeEventListener("scroll", updateVisible);
      ro.disconnect();
    };
  }, [updateVisible]);
  useLayoutEffect(() => bump(), [version, zoom, selection, hoverId, bump]);

  /**
   * Scrolls to page `index`: centred when the whole page fits, top-aligned when
   * it does not. The offset has to include the padding above the first page, or
   * every jump lands one padding short and leaves the join between two pages
   * across the middle of the viewport.
   */
  const goToPage = useCallback(
    (index: number) => {
      const sc = scrollRef.current;
      if (!sc || !layout) return;
      const clamped = Math.max(0, Math.min(layout.paged.totalPages - 1, index));
      sc.scrollTop = scrollTopForPage(clamped, layout.page.height, zoom, sc.clientHeight);
    },
    [layout, zoom]
  );

  // Zooming should magnify what you are looking at, not send you somewhere
  // else. Scroll offsets are in scaled pixels, so leaving scrollTop alone as
  // the scale changes moves the document under the viewport.
  const lastZoom = useRef(zoom);
  /** Page to land on once the new zoom has been applied, for "fit page". */
  const snapPage = useRef<number | null>(null);
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    if (!sc || lastZoom.current === zoom) return;
    const ratio = zoom / lastZoom.current;
    lastZoom.current = zoom;
    const centre = sc.scrollTop + sc.clientHeight / 2;
    sc.scrollTop = Math.max(0, centre * ratio - sc.clientHeight / 2);
  }, [zoom]);

  // Declared after the effect above so it runs after it: "fit page" means a
  // whole page in view, and preserving the old scroll centre through the zoom
  // change is exactly what would leave you looking at the join between two.
  useLayoutEffect(() => {
    if (snapPage.current === null) return;
    const target = snapPage.current;
    snapPage.current = null;
    goToPage(target);
  }, [zoom, goToPage]);

  // The toolbar's fit buttons: the canvas is what knows the scroller and the
  // page you are on, so it supplies the implementation.
  useEffect(() => {
    session.fit = (mode) => {
      const sc = scrollRef.current;
      if (!sc || !layout) return;
      const next = fitZoom(mode, sc, layout.page);
      if (mode === "page") snapPage.current = currentPage;
      if (next === zoom) {
        // Already at that zoom: the button still has to do the landing part.
        if (mode === "page") {
          snapPage.current = null;
          goToPage(currentPage);
        }
        return;
      }
      session.store.setZoom(next);
    };
    return () => {
      session.fit = null;
    };
  }, [session, layout, currentPage, zoom, goToPage]);

  // Switching modes re-paginates, so the pixel offset is meaningless but the
  // page number is not: come back to the page you left from.
  const lastMode = useRef(mode);
  useLayoutEffect(() => {
    if (lastMode.current === mode) return;
    lastMode.current = mode;
    pendingPage.current = currentPage;
  }, [mode, currentPage]);
  useEffect(() => {
    if (pendingPage.current === null || !layout) return;
    const target = pendingPage.current;
    pendingPage.current = null;
    goToPage(target);
  }, [layout, goToPage]);

  /** Which page a node landed on, from the layout rather than the DOM. */
  const pageOfNode = useCallback(
    (id: string): number | null => {
      if (!layout) return null;
      const covers = (sectionKey: string, top: number) => {
        for (const page of layout.paged.pages) {
          for (const f of page.fragments) {
            if (f.sectionKey === sectionKey && top >= f.from - 1 && top < f.to + 1) return page.index;
          }
        }
        return null;
      };
      for (const [sectionKey, geom] of Object.entries(layout.geometry)) {
        if (layout.nodesByKey.get(sectionKey)?.nodeId === id) {
          const p = covers(sectionKey, 0);
          if (p !== null) return p;
        }
        for (const [key, g] of Object.entries(geom.nodes)) {
          if (layout.nodesByKey.get(key)?.nodeId !== id) continue;
          const p = covers(sectionKey, g.top);
          if (p !== null) return p;
        }
      }
      return null;
    },
    [layout]
  );

  // Scroll a node into view when requested (from layers, issues, insertion).
  //
  // Each request is honoured exactly once. The effect has to watch the visible
  // range, because a node on an unrendered page has no element to scroll to
  // until that range widens — but re-acting on every scroll would drag the
  // canvas back to the last revealed block the moment you tried to leave it.
  const reveal_ = useRef<{ seq: number; done: boolean; tries: number }>({ seq: -1, done: true, tries: 0 });
  useEffect(() => {
    const scroller = scrollRef.current;
    const pages = pagesRef.current;
    if (!reveal || !pages || !scroller || !layout) return;
    if (reveal_.current.seq !== reveal.seq) reveal_.current = { seq: reveal.seq, done: false, tries: 0 };
    if (reveal_.current.done) return;

    const el = pages.querySelector<HTMLElement>(`[data-node="${CSS.escape(reveal.id)}"]:not([data-culled])`);
    if (el) {
      reveal_.current.done = true;
      const r = el.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      // Instant, not smooth: a smooth scroll keeps animating after the user
      // starts scrolling themselves, and lands wherever it was going — which
      // reads as the canvas dragging itself back.
      if (r.top < sr.top + 40 || r.bottom > sr.bottom - 40) el.scrollIntoView({ block: "center", behavior: "instant" as ScrollBehavior });
      return;
    }

    // The node's page is not rendered — or, just after an insertion, the layout
    // has not caught up with it at all. Either way this effect runs again when
    // the layout or the visible range changes, so keep trying for a few passes
    // rather than giving up on the first stale layout; a request for a node
    // that really is gone stops after that instead of pinning the canvas.
    const index = pageOfNode(reveal.id);
    if (index === null) {
      if (++reveal_.current.tries > 8) reveal_.current.done = true;
      return;
    }
    scroller.scrollTop = scrollTopForPage(index, layout.page.height, zoom, scroller.clientHeight);
    setVisibleRange(([first, last]) => (index >= first && index <= last ? [first, last] : [Math.max(0, index - 1), Math.min(layout.paged.totalPages - 1, index + 1)]));
  }, [reveal, version, visibleRange, layout, zoom, pageOfNode]);

  // Says where it went. A block inserted from the palette or the library can
  // land on a page you were not looking at, and a selection outline on its own
  // is easy to miss when the canvas has just scrolled to it; the flash is what
  // connects "I clicked Insert" to "there it is". It is re-applied whenever the
  // page rebuilds, because the page DOM is replaced wholesale on every layout.
  const [flash, setFlash] = useState<{ id: string; seq: number; shown: boolean } | null>(null);
  useEffect(() => {
    if (reveal) setFlash({ id: reveal.id, seq: reveal.seq, shown: false });
  }, [reveal]);
  // The countdown starts when the block is first on screen, not when it was
  // asked for: an insertion has to be re-measured and re-laid-out before it
  // exists in the DOM at all, and on a long document that can take longer than
  // the flash itself — so timing from the request meant the one case that most
  // needed the highlight was the one case that never showed it.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash((f) => (f?.seq === flash.seq ? null : f)), flash.shown ? 1400 : 8000);
    return () => clearTimeout(t);
  }, [flash]);
  useLayoutEffect(() => {
    const pages = pagesRef.current;
    if (!pages) return;
    for (const el of pages.querySelectorAll(".bpe-flash")) el.classList.remove("bpe-flash");
    if (!flash) return;
    const els = pages.querySelectorAll(`[data-node="${CSS.escape(flash.id)}"]`);
    for (const el of els) el.classList.add("bpe-flash");
    if (els.length && !flash.shown) setFlash({ ...flash, shown: true });
  }, [flash, version, visibleRange, zoom]);

  /* ----------------------------- pointer ----------------------------- */

  const pageFirst = useCallback((el: Element) => el.closest(".bp-page")?.classList.contains("bp-page--first") ?? false, []);

  /** Places the insertion caret where the pointer is, if that is a real place. */
  const placeCaret = useCallback(
    (x: number, y: number) => {
      const canvas = pagesRef.current;
      const origin = innerRef.current;
      if (!canvas || !origin) return;
      const target = computeInsertion(x, y, { template: session.store.template, registry: session.registry, canvas, overlayOrigin: origin }, pageFirst);
      session.ui.patch({ insertion: target?.line ? { parentId: target.parentId, region: target.region, index: target.index, line: target.line } : null });
    },
    [session, pageFirst]
  );

  const onPointerDown = (e: React.PointerEvent) => {
    if (mode !== "design") return;
    if ((e.target as HTMLElement).closest(".bpe-overlay, .bpe-inline-editor, .bpe-bubble, .bpe-block-toolbar")) return;
    const hit = nodeIdAt(e.target);
    if (editingId && hit?.id !== editingId) session.store.setEditing(null);
    // Every click in the canvas answers "where would the next block go", so a
    // block from the palette lands where you were looking rather than at the
    // end of the document.
    placeCaret(e.clientX, e.clientY);
    if (!hit) {
      if (!e.shiftKey) session.store.select([]);
      scrollRef.current?.focus({ preventScroll: true });
      return;
    }
    // Repeater iterations and structural columns select their logical node.
    const id = hit.id;
    if (e.shiftKey || e.metaKey || e.ctrlKey) session.store.toggleSelect(id);
    else if (!selection.includes(id)) session.store.select([id]);
    // Drag to move (after threshold) when pressing on an already-selected block.
    const node = session.store.getNode(id);
    if (node && !node.locked && node.type !== "column" && !editingId) {
      beginDrag(session, e, { kind: "move", payload: id, label: nodeLabel(session, node), icon: session.registry.get(node.type)?.icon }, { threshold: 6 });
    }
    scrollRef.current?.focus({ preventScroll: true });
  };

  const onDoubleClick = (e: React.MouseEvent) => {
    if (mode !== "design") return;
    const hit = nodeIdAt(e.target);
    if (!hit) return;
    const def = session.registry.get(hit.type);
    if (def?.canvas?.inlineEdit) {
      const node = session.store.getNode(hit.id);
      if (node?.locked) return;
      session.store.setEditing(hit.id);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (mode !== "design" || drag) return;
    const hit = nodeIdAt(e.target);
    const id = hit && !(e.target as HTMLElement).closest(".bpe-overlay") ? hit.id : null;
    if (id !== hoverId) session.store.hover(id);
  };

  /* ------------------------------ keyboard --------------------------- */

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (mode !== "design" || editingId) return;
    const target = e.target as HTMLElement;
    if (target.closest("input, textarea, select, [contenteditable=true]")) return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (mod && key === "z" && !e.shiftKey) return void (e.preventDefault(), actions.undo());
    if ((mod && key === "z" && e.shiftKey) || (mod && key === "y")) return void (e.preventDefault(), actions.redo());
    if (mod && key === "c") return void (e.preventDefault(), actions.copySelection());
    if (mod && key === "x") return void (e.preventDefault(), actions.cutSelection());
    if (mod && key === "v") return void (e.preventDefault(), actions.pasteFromClipboard());
    if (mod && key === "d") return void (e.preventDefault(), actions.duplicateSelection());
    if (mod && key === "s") return void (e.preventDefault(), actions.save());
    if (mod && key === "a") return void (e.preventDefault(), session.store.select(session.store.template.body.map((s) => s.id)));
    if (e.key === "Delete" || e.key === "Backspace") return void (e.preventDefault(), actions.deleteSelection());
    if (e.key === "Escape") return void session.store.select([]);
    if (e.key === "Enter") {
      const id = selection[0];
      const node = id ? session.store.getNode(id) : undefined;
      if (node && session.registry.get(node.type)?.canvas?.inlineEdit) {
        e.preventDefault();
        session.store.setEditing(id!);
      }
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      if (e.altKey) actions.moveSelection(e.key === "ArrowUp" ? -1 : 1);
      else actions.selectSibling(e.key === "ArrowUp" ? -1 : 1);
      return;
    }
    if (e.key === "ArrowLeft") return void (e.preventDefault(), actions.selectParent());
    if (e.key === "ArrowRight") return void (e.preventDefault(), actions.selectFirstChild());
    if (mod && (e.key === "=" || e.key === "+")) return void (e.preventDefault(), session.store.setZoom(zoom + 0.1));
    if (mod && e.key === "-") return void (e.preventDefault(), session.store.setZoom(zoom - 0.1));
    if (mod && e.key === "0") return void (e.preventDefault(), session.store.setZoom(1));
  };

  /* ---------------------------- drag & drop -------------------------- */


  useEffect(() => {
    registerDragHandlers(session, {
      resolve: (x, y, d) => {
        const canvas = pagesRef.current;
        const origin = innerRef.current;
        if (!canvas || !origin) return null;
        const sr = scrollRef.current!.getBoundingClientRect();
        if (x < sr.left || x > sr.right || y < sr.top || y > sr.bottom) return null;
        return computeDropTarget(x, y, d, { template: session.store.template, registry: session.registry, canvas, overlayOrigin: origin }, pageFirst);
      },
      drop: (target, d) => {
        if (target.textTargetId && d.kind === "field") {
          // The formatted form, not the bare path: a token dropped into a
          // sentence is read as text, so a money field belongs there as
          // `total | currency` and not as 482310.4.
          const p = d.payload as { expr: string; bound?: string; label: string };
          const expr = p.bound ?? p.expr;
          const node = session.store.getNode(target.textTargetId);
          if (!node) return;
          if (node.type === "heading") {
            const text = String(node.props.text ?? "");
            session.store.dispatch({ type: "setProp", id: node.id, path: "text", value: `${text}${text && !text.endsWith(" ") ? " " : ""}{{ ${expr} }}` }, { label: "Insert field" });
          } else {
            const doc: RichTextDoc = isRichTextDoc(node.props.content) ? structuredClone(node.props.content) : richTextFromTemplate("");
            const last = doc.content[doc.content.length - 1];
            if (last && last.type === "paragraph") {
              if (last.content.length) last.content.push({ type: "text", text: " " });
              last.content.push({ type: "token", expr });
            } else doc.content.push({ type: "paragraph", content: [{ type: "token", expr }] });
            session.store.dispatch({ type: "setProp", id: node.id, path: "content", value: doc }, { label: "Insert field" });
          }
          session.store.select([node.id]);
          return;
        }
        const path = target.parentId ? [...(session.store.locationOf(target.parentId)?.path ?? []), target.index] : [target.index];
        const at = { region: target.region, path };
        if (d.kind === "move") {
          actions.moveNode(String(d.payload), at);
          return;
        }
        const node: Node | undefined = nodeForDrop(d, {
          registry: session.registry,
          template: session.store.template,
          createNode: (type, props) => createNode(session.registry, type, { props }),
          createRow: (layout) => createRow(layout),
          cloneLibrary: (id) => {
            const item = session.store.template.library?.find((l) => l.id === id) ?? session.options.get().library?.find((l) => l.id === id);
            return item ? cloneWithNewIds(item.node) : undefined;
          }
        });
        if (node) {
          actions.insertNodeAt(node, at, `Add ${session.registry.get(node.type)?.name ?? node.type}`);
        }
      },
      autoScroll: (x, y) => {
        const sc = scrollRef.current;
        if (!sc) return;
        const r = sc.getBoundingClientRect();
        const edge = 48;
        if (y < r.top + edge && y > r.top - 20) sc.scrollTop -= 14;
        else if (y > r.bottom - edge && y < r.bottom + 20) sc.scrollTop += 14;
      }
    });
    return () => registerDragHandlers(session, null);
  }, [session, actions, pageFirst]);

  /* ------------------------------ render ----------------------------- */

  const pages = useMemo(() => {
    if (!layout) return null;
    return Array.from({ length: layout.paged.totalPages }, (_, i) => (
      <PageView key={i} layout={layout} index={i} version={version} zoom={zoom} visible={i >= visibleRange[0] && i <= visibleRange[1]} interactive={interactiveCharts} />
    ));
  }, [layout, version, zoom, visibleRange, interactiveCharts]);

  const cls = ["bpe-canvas", mode === "design" && "bpe-canvas--design", guides && "bpe-canvas--guides", drag && "bpe-canvas--dragging"].filter(Boolean).join(" ");

  return (
    <div className={cls}>
      {mode === "preview" ? (
        <div className="bpe-preview-bar">
          <Icon name="eye" size={14} />
          <span>Exactly what the PDF will contain, with the selected sample data. Editing panels are hidden until you go back.</span>
          <span style={{ marginLeft: "auto" }} />
          <Button size="sm" variant="ghost" icon="pencil" onClick={() => session.setMode("design")}>Back to design</Button>
        </div>
      ) : null}
      <div ref={scrollRef} className="bpe-canvas__scroll" tabIndex={0} role="region" aria-label="Document canvas" onKeyDown={onKeyDown} onPointerDown={onPointerDown} onDoubleClick={onDoubleClick} onPointerMove={onPointerMove} onPointerLeave={() => session.store.hover(null)}>
        <div ref={innerRef} className="bpe-canvas__inner">
          <div ref={pagesRef} className={`bpe-pages bp-doc ${mode === "design" ? "bp-doc--design" : ""}`}>{pages}</div>
          {layout && innerRef.current && pagesRef.current ? <Overlays container={pagesRef.current} origin={innerRef.current} actions={actions} zoom={zoom} /> : null}
          {editingId && pagesRef.current && innerRef.current && mode === "design" ? <InlineEditor key={editingId} nodeId={editingId} container={pagesRef.current} origin={innerRef.current} zoom={zoom} onClose={() => session.store.setEditing(null)} /> : null}
          {!layout ? <div className="bpe-empty" style={{ height: 400 }}>Laying out…</div> : null}
        </div>
      </div>
      <div ref={measureHostRef} style={{ position: "absolute", left: 0, top: 0, width: 0, height: 0, overflow: "hidden" }} aria-hidden="true" />
      <div className="bpe-canvas__bar">
        {/* The breadcrumb and the "select a block" prompt are instructions for
            editing. In preview there is nothing to select, so they would be
            telling you to do something the mode does not allow. */}
        {mode === "design" ? <Breadcrumb /> : <span className="bpe-breadcrumb bpe-muted">Previewing “{sampleLabel}”</span>}
        <div className="bpe-canvas__bar-end">
          {layout && layout.paged.totalPages > 1 ? (
            <nav className="bpe-pager" aria-label="Page navigation">
              <Button size="sm" variant="ghost" icon="chevron-left" tooltip="Previous page" disabled={currentPage === 0} onClick={() => goToPage(currentPage - 1)} />
              <span className="bpe-pager__label">Page {currentPage + 1} of {layout.paged.totalPages}</span>
              <Button size="sm" variant="ghost" icon="chevron-right" tooltip="Next page" disabled={currentPage >= layout.paged.totalPages - 1} onClick={() => goToPage(currentPage + 1)} />
            </nav>
          ) : layout ? (
            <span>1 page</span>
          ) : null}
          {layout?.warnings.length ? (
            <span className="bpe-badge bpe-badge--warning" title={layout.warnings.map((w) => w.message).join("\n")}>
              <Icon name="alert-triangle" size={11} /> {layout.warnings.length}
            </span>
          ) : null}
          {mode === "design" ? <Button size="sm" variant="ghost" icon="ruler" active={guides} tooltip="Show page margins" onClick={() => session.ui.patch({ guides: !guides })} /> : null}
          {mode === "design" ? <DesignLegend sampleLabel={sampleLabel} /> : null}
        </div>
      </div>
      {toast ? (
        <div className="bpe-toast" role="status">
          <Icon name={toast.tone === "error" ? "alert-circle" : toast.tone === "success" ? "check-circle" : "info"} />
          {toast.message}
        </div>
      ) : null}
      {drag ? (
        <div className="bpe-drag-ghost" style={{ left: drag.x, top: drag.y }}>
          <Icon name={drag.icon ?? "box"} /> {drag.label}
        </div>
      ) : null}
    </div>
  );
}
